// Shadow Network — Direct Messages: 1:1 chat.
// Route: /shadow/mensajes/:userId
// Polls every 5s for new messages. Auto-marks inbound as read on every fetch
// (the backend GET endpoint does this transparently).
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tShadow } from "../i18n/shadow-social";
import { UserAvatar } from "../components/shadow/UserAvatar";
import { API_BASE as API } from "../lib/api";

const POLL_MS = 5000;
const MAX_LEN = 1000;

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const ShadowMessagesChatPage = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [peer, setPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const lastMsgIdRef = useRef(null);

  const scrollToBottom = useCallback((behavior = "auto") => {
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior });
    });
  }, []);

  const fetchThread = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await axios.get(`${API}/api/shadow/messages/${userId}`);
      setPeer(data?.peer || null);
      const next = data?.messages || [];
      const last = next[next.length - 1];
      const newLastId = last?.id || null;
      const grew = newLastId && newLastId !== lastMsgIdRef.current;
      lastMsgIdRef.current = newLastId;
      setMessages(next);
      if (grew) scrollToBottom("smooth");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) setError("No puedes enviarte mensajes a ti mismo.");
      else if (status === 404) setError("Usuario no encontrado.");
      else if (status === 401 || status === 403) setError("Sesión caducada.");
    } finally {
      setLoading(false);
    }
  }, [userId, scrollToBottom]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchThread().then(() => scrollToBottom("auto"));
    const id = setInterval(fetchThread, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchThread, scrollToBottom]);

  const send = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/api/shadow/messages/${userId}`,
        { content: content.slice(0, MAX_LEN) }
      );
      if (data?.message) {
        setMessages((prev) => [...prev, data.message]);
        lastMsgIdRef.current = data.message.id;
        scrollToBottom("smooth");
      }
      setDraft("");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (detail === "cannot_message_self")
        setError("No puedes enviarte mensajes a ti mismo.");
      else if (detail === "user_not_found") setError("Usuario no encontrado.");
      else if (status === 401 || status === 403) setError("Sesión caducada.");
      else if (!status) setError("Error de red. Reintenta.");
      else setError(`No se pudo enviar (HTTP ${status}).`);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-sm text-white/65 mb-4">Acceso de operador requerido.</p>
          <Link to="/" className="text-amber-400 underline">Volver</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-black text-white" data-testid="shadow-messages-chat">
      {/* Header */}
      <header className="border-b border-white/5 bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3 md:px-8">
          <Link
            to="/shadow/mensajes"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
            data-testid="chat-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] hidden sm:inline">
              {tShadow("mensajes", lang)}
            </span>
          </Link>
          {peer && (
            <Link
              to={peer.username ? `/shadow/perfil/${peer.username}` : "#"}
              className="flex items-center gap-2 min-w-0 flex-1 hover:opacity-80 transition-opacity"
            >
              <UserAvatar user={peer} size="sm" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{peer.name}</p>
                {peer.username && (
                  <p className="truncate text-[11px] text-white/45">@{peer.username}</p>
                )}
                <div
                  className="mt-0.5 flex items-center gap-1"
                  data-testid="chat-encryption-badge"
                >
                  <span aria-hidden="true" className="text-[9px]">🔒</span>
                  <span className="font-mono text-[9px] tracking-[0.05em] text-emerald-300/60">
                    {tShadow("cifradoEndToEnd", lang)}
                  </span>
                </div>
              </div>
            </Link>
          )}
        </div>
      </header>

      {/* Messages */}
      <main
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 md:px-8"
        data-testid="messages-scroll"
      >
        <div className="mx-auto max-w-2xl space-y-2">
          {loading ? (
            <p className="text-center text-white/45 text-sm font-mono py-12">…</p>
          ) : messages.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
              <p className="text-sm text-white/55">{tShadow("sinMensajes", lang)}</p>
              <p className="text-[11px] text-white/35 mt-1">Escribe el primer mensaje 👇</p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === user.id;
              return (
                <div
                  key={m.id}
                  data-testid={`message-${m.id}`}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${
                      mine
                        ? "bg-gradient-to-br from-amber-500/[0.18] to-amber-400/[0.10] border border-amber-400/30 text-amber-50"
                        : "bg-white/[0.05] border border-white/10 text-white/90"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words leading-snug">{m.content}</p>
                    <p
                      className={`mt-1 text-[9px] font-mono uppercase tracking-[0.15em] ${
                        mine ? "text-amber-200/55 text-right" : "text-white/35"
                      }`}
                    >
                      {formatTime(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      {/* Composer */}
      <footer className="border-t border-white/5 bg-black/85 backdrop-blur-xl">
        <div className="mx-auto max-w-2xl px-4 py-3 md:px-8">
          {error && (
            <p
              className="mb-2 rounded-lg border border-rose-500/30 bg-rose-500/[0.08] px-3 py-2 text-xs text-rose-200"
              data-testid="chat-error"
            >
              ⚠️ {error}
            </p>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
              onKeyDown={onKeyDown}
              placeholder={tShadow("escribeMensaje", lang)}
              rows={1}
              data-testid="chat-input"
              className="flex-1 resize-none rounded-2xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-amber-400/40 focus:outline-none"
              style={{ maxHeight: 120 }}
            />
            <button
              type="button"
              onClick={send}
              disabled={!draft.trim() || sending}
              data-testid="chat-send-btn"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-500 text-black transition-all hover:from-amber-300 hover:to-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={tShadow("enviar", lang)}
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1 text-[10px] text-white/30 font-mono">
            {tShadow("enterParaEnviar", lang)} · {draft.length}/{MAX_LEN}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ShadowMessagesChatPage;
